/**
 * The one way notifications are turned on or off, wherever the button lives.
 *
 * Every entry point - the waiting panel, the off-turn nudge, the bell on the
 * home page - runs this same flow, so there is exactly one place that knows
 * the order of operations: permission, worker, subscription, server. The
 * browser APIs arrive through `scope` so a test can stand in a browser's
 * worth of push machinery without one.
 *
 * Nothing here touches localStorage. Whether notifications are on is a fact
 * the browser already holds (permission plus subscription), and a second copy
 * would only learn to disagree with it.
 */

/** What a status check or an attempt to enable can come back as. */
export const STATES = ['unsupported', 'needs-install', 'blocked', 'off', 'on', 'failed'];

/**
 * Whether this is one of Apple's touch platforms.
 *
 * Two things want the answer and they must never disagree: whether push needs a
 * home-screen install first, and which way round the system draws its share and
 * copy glyphs. iPadOS reports itself as a Mac, which is what the touch-points
 * check catches.
 */
export function isApple(scope = globalThis) {
    const nav = scope && scope.navigator;
    const ua = (nav && nav.userAgent) || '';
    return /iPad|iPhone|iPod/.test(ua)
        || (/Macintosh/.test(ua) && Boolean(nav) && nav.maxTouchPoints > 1);
}

export function createNotify({ publicKey = '', scope = globalThis, fetchImpl = null } = {}) {
    const nav = scope.navigator;
    const send = fetchImpl || (typeof scope.fetch === 'function' ? scope.fetch.bind(scope) : null);

    const supported = Boolean(
        publicKey
        && send
        && nav
        && 'serviceWorker' in nav
        && 'PushManager' in scope
        && 'Notification' in scope,
    );

    // Safari only delivers push to an installed home-screen app, so outside one
    // the honest answer is instructions, not a permission prompt.
    const isIOS = isApple(scope);
    const standalone = Boolean(
        (scope.matchMedia && scope.matchMedia('(display-mode: standalone)').matches)
        || (nav && nav.standalone === true),
    );
    // Checked before `supported`, not as its fallback: some Safari versions
    // expose the push interfaces in a tab and still never deliver to one, so
    // an iOS tab gets the instructions whatever the interfaces claim.
    const needsInstall = isIOS && !standalone;

    async function subscription() {
        const reg = await nav.serviceWorker.getRegistration();
        if (!reg) return null;
        return reg.pushManager.getSubscription();
    }

    return {
        /** Where this browser stands, without changing anything. */
        async status() {
            if (needsInstall) return 'needs-install';
            if (!supported) return 'unsupported';
            if (scope.Notification.permission === 'denied') return 'blocked';
            try {
                return (await subscription()) ? 'on' : 'off';
            } catch {
                return 'off';
            }
        },

        /**
         * Runs the flow through to a stored subscription, and answers with
         * where it got to. 'failed' is the one retryable answer: everything
         * reached worked except the last step, and nothing was kept - a
         * subscription the server never heard of would be worse than none.
         */
        async enable() {
            if (needsInstall) return 'needs-install';
            if (!supported) return 'unsupported';

            const permission = await scope.Notification.requestPermission();
            if (permission !== 'granted') return 'blocked';

            try {
                await nav.serviceWorker.register('/sw.js');
                // register() can resolve before the worker may take a
                // subscription; ready waits for one that can.
                const reg = await nav.serviceWorker.ready;

                const sub = await reg.pushManager.subscribe({
                    userVisibleOnly: true,
                    applicationServerKey: decodeKey(scope, publicKey),
                });

                const res = await send('/push', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(sub.toJSON()),
                });
                if (!res.ok) {
                    await sub.unsubscribe().catch(() => {});
                    return 'failed';
                }
                return 'on';
            } catch {
                return 'failed';
            }
        },

        /**
         * Lets go of the subscription on both ends. The browser side goes
         * first: a mailbox the push service has forgotten but the server still
         * knocks on costs one failed send and gets pruned, while the reverse
         * would keep a live mailbox nobody is allowed to stop.
         */
        async disable() {
            try {
                const sub = await subscription();
                if (!sub) return 'off';

                const endpoint = sub.endpoint;
                await sub.unsubscribe().catch(() => {});
                await send('/push', {
                    method: 'DELETE',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ endpoint }),
                }).catch(() => {});
            } catch {
                // Nothing left to let go of.
            }
            return 'off';
        },

        /**
         * Re-files the current subscription with the server, quietly. Run on
         * page load when already on: it heals a server record lost to a
         * failed request or a browser-side rotation the worker missed, at the
         * cost of one small request from a player who opted in.
         */
        async resync() {
            try {
                const sub = await subscription();
                if (!sub) return;
                await send('/push', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(sub.toJSON()),
                });
            } catch {
                // The next send failing is what this was trying to prevent,
                // not something it can do anything about here.
            }
        },
    };
}

/** The VAPID key, from base64url to the bytes subscribe() wants. */
function decodeKey(scope, base64) {
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const raw = scope.atob(padded.replace(/-/g, '+').replace(/_/g, '/'));
    const bytes = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
    return bytes;
}
